import React, { useEffect, useState } from 'react';
import { useHistory } from 'react-router-dom';
import PropTypes from 'prop-types';

import {
  sendEmailVerificationEvent,
} from '@kueski-analytics/events/email_verification';
import useMerchantPaymentQuery from 'Hooks/useMerchantPaymentQuery';
import Urls from 'Containers/Urls';
import { LoggerFactory, DOMAIN, FEATURE } from 'Services/Logger';
import { sendEmailVerification } from 'Services/VerifyEmail/EmailVerification';
import useCountDown from 'Redesign_Funnel/hooks/useCountDown';
import { removeSignInVerifyEmailData } from 'Utils/verifyEmail';
import useIsEmailVerified from 'Utils/hooks/useIsEmailVerified';
import useCustomerLendingApplication from 'Hooks/useCustomerLendingApplication';
import { isEmbeddedAmazon } from 'Utils/mobile';
import { amplitudeProductTypeByPaymentTypeAndChannel } from 'Utils/mappers';
import useProtectedApplication from 'Hooks/useProtectedApplication';
import { useLocalStorage } from 'Hooks/useStorage';

import { EMAIL_VERIFICATION_STATUS, EMAIL_VERIFICATION_STATUS_VERIFIED } from '../EmailVerification.constants';
import { SendEmailUI } from '../components/SendEmailUI';

const emailVerifiedMessage = 'Email verified';

const Logger = LoggerFactory.getLogger(DOMAIN.CONSUMER_ENABLEMENT);

const ResendEmailVerification = ({ sendByDefault }) => {
  const { data: paymentData } = useMerchantPaymentQuery();
  const email = localStorage.getItem('email');
  const history = useHistory();

  const [isManualRefetching, setIsManualRefetching] = useState(false);

  const { isEmailVerified, refetch, hasPollingLimitReached } = useIsEmailVerified({
    enabled: true, shouldPoll: true,
  });

  const { data: lendingApplication } = useCustomerLendingApplication();
  const [isResendDisabled, setIsResendDisabled] = useState(true);
  const [couldValidateButton, setCouldValidateButton] = useState(false);
  const [canChangeMessage, setCanChangeMessage] = useState(false);

  const isEmbeddedAmzApp = isEmbeddedAmazon();
  const { data } = useProtectedApplication();
  const { countDownHumanize, countDownIsOver, startCounter } = useCountDown(0, 1000, true);
  const [
    emailVerificationStorage,
    setEmailVerificationStorage,
  ] = useLocalStorage(EMAIL_VERIFICATION_STATUS);

  const sendEmailVerificationSuccessEvent = () => {
    const eventData = {
      application_id: data?.application_process_id,
      is_embedded_amz: isEmbeddedAmzApp,
      kueski_user_id: data?.customer_id ?? '',
      merchant_id: paymentData?.payment?.merchantId,
      merchant_name: paymentData?.payment?.merchantName,
      page_name: 'verify_email',
      product_type: amplitudeProductTypeByPaymentTypeAndChannel(
        data?.payment_type,
        data?.channel,
        true,
      ),
      purchase_amount: paymentData?.payment?.amount,
      recurrent: data?.recurrent,
    };
    sendEmailVerificationEvent(eventData);
  };

  const sendEmail = () => {
    sendEmailVerification({ user_id: data?.customer_id ?? '0' })
      .then((response) => {
        const isSuccessResponse = (response && response.status === 'success') || false;
        if (isSuccessResponse) {
          const secondsLeft = response.data.seconds_left;
          startCounter(secondsLeft * 1000);
          if (response.data.message === emailVerifiedMessage) {
            removeSignInVerifyEmailData();
            history.push(Urls.root);
            return;
          }
        }
        setCouldValidateButton(true);
      })
      .catch((err) => {
        setIsResendDisabled(false);
        setCouldValidateButton(false);

        Logger.warn(
          'Error Resend Verification Email',
          `${FEATURE.LOGIN}`,
          {
            error: err?.response?.data?.message,
          },
        );
      });
  };

  useEffect(() => {
    if (couldValidateButton) setIsResendDisabled(!countDownIsOver);
  }, [countDownIsOver, couldValidateButton]);

  useEffect(() => {
    if (sendByDefault) {
      if (paymentData && data) {
        sendEmail();
        sendEmailVerificationSuccessEvent();
      }
    } else setIsResendDisabled(false);
  }, [data, paymentData, sendByDefault]);

  const redirectTo = lendingApplication?.can_continue ? Urls.apply : Urls.home;

  useEffect(() => {
    if (isEmailVerified) history.push(redirectTo);
  }, [isEmailVerified]);

  useEffect(() => {
    if (emailVerificationStorage === EMAIL_VERIFICATION_STATUS_VERIFIED) {
      setEmailVerificationStorage('');
      history.push(redirectTo);
    }
  }, [emailVerificationStorage]);

  const resendEmail = () => {
    setIsResendDisabled(true);
    sendEmail();
  };

  const verifyEmailStatus = async () => {
    setIsManualRefetching(true);
    try {
      await refetch();
    } finally {
      setIsManualRefetching(false);
      setCanChangeMessage(true);
    }
  };

  return (
    <SendEmailUI
      isDefaultMessage={canChangeMessage && isEmailVerified === false}
      email={email}
      onResend={resendEmail}
      onVerified={verifyEmailStatus}
      showVerifiedButton={hasPollingLimitReached}
      resendDisabled={isResendDisabled}
      verifiedDisabled={isManualRefetching}
      countDown={!countDownIsOver ? ` en ${countDownHumanize}` : ''}
    />
  );
};

ResendEmailVerification.propTypes = {
  sendByDefault: PropTypes.bool,
};

ResendEmailVerification.defaultProps = {
  sendByDefault: false,
};

export default ResendEmailVerification;
